import { Link } from "react-router-dom";
import type { CampaignSummary } from "@/types/campaign";
import type { FieldGpsState } from "../useFieldGps";
import { usePwaInstall } from "../usePwaInstall";

interface FieldHomeProps {
  mission: CampaignSummary | null;
  missionLoading: boolean;
  missionError: string | null;
  gps: FieldGpsState;
  newTreeHref: string;
  onNavigate: (href: string) => void;
}

const acoesRapidas = [
  { icon: "ri-camera-line", label: "Fotos de exemplar", href: "/coletor/exemplar", bg: "bg-[#1B6E4F]" },
  { icon: "ri-qr-scan-2-line", label: "Ler QR Code", href: "/coletor/qrcode", bg: "bg-[#3F4A52]" },
  { icon: "ri-map-pin-line", label: "Exemplares próximos", href: "/coletor/mapa", bg: "bg-[#0052CC]" },
  { icon: "ri-clipboard-line", label: "Nova vistoria", href: "/coletor/vistoria", bg: "bg-[#0052CC]" },
  { icon: "ri-book-open-line", label: "Como usar o coletor", href: "/manual/coletor", bg: "bg-[#B7791F]" },
];

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("pt-BR");
}

export default function FieldHome({ mission, missionLoading, missionError, gps, newTreeHref, onNavigate }: FieldHomeProps) {
  const pwa = usePwaInstall();
  return (
    <main className="px-4 pb-20 pt-4 max-w-lg md:max-w-2xl mx-auto">
      <div className="mb-5">
        <h2 className="text-xl font-heading font-bold text-[#3F4A52]">Coletor de Campo</h2>
        <p className="text-sm text-[#3F4A52]/70 mt-1">
          Cadastro e vistoria em campo. Por enquanto as gravações exigem conexão e vão direto ao servidor.
        </p>
      </div>

      {pwa.canPrompt && (
        <button
          onClick={() => void pwa.install()}
          className="w-full mb-5 flex items-center gap-3 p-3 rounded-xl bg-[#1B6E4F] text-white text-left"
        >
          <i className="ri-smartphone-line text-2xl" />
          <span className="flex-1">
            <span className="block text-sm font-heading font-bold">Instalar o FloraIQ neste aparelho</span>
            <span className="block text-xs opacity-90">Abre direto no coletor, em tela cheia.</span>
          </span>
          <i className="ri-download-2-line text-xl" />
        </button>
      )}
      {pwa.showIosHint && (
        <p className="mb-5 text-xs text-[#3F4A52]/80 bg-white border border-[#E8EAED] rounded-xl p-3">
          Para instalar no iPhone: toque em <strong>Compartilhar</strong> e depois em <strong>Adicionar à Tela de Início</strong>.
        </p>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-5">
        <Link
          to={newTreeHref}
          className="flex flex-col items-center gap-2 p-4 rounded-xl bg-white border border-[#E8EAED] hover:shadow-md transition-all active:scale-95"
        >
          <div className="w-11 h-11 rounded-xl bg-[#1B6E4F] flex items-center justify-center">
            <i className="ri-add-circle-line text-white text-xl" />
          </div>
          <span className="text-xs font-medium text-[#3F4A52] text-center leading-tight">Nova árvore</span>
        </Link>
        {acoesRapidas.map((acao) => (
          <button
            key={acao.href}
            onClick={() => onNavigate(acao.href)}
            className="flex flex-col items-center gap-2 p-4 rounded-xl bg-white border border-[#E8EAED] hover:shadow-md transition-all active:scale-95"
          >
            <div className={`w-11 h-11 rounded-xl ${acao.bg} flex items-center justify-center`}>
              <i className={`${acao.icon} text-white text-xl`} />
            </div>
            <span className="text-xs font-medium text-[#3F4A52] text-center leading-tight">{acao.label}</span>
          </button>
        ))}
      </div>

      <div className="mb-5">
        <h3 className="text-sm font-heading font-bold text-[#3F4A52] mb-3">Missão ativa</h3>
        {missionLoading ? (
          <p className="text-xs text-[#3F4A52]/60">Carregando...</p>
        ) : missionError ? (
          <p className="text-xs text-[#D32F2F]">Não foi possível carregar as missões: {missionError}</p>
        ) : !mission ? (
          <div className="bg-white border border-dashed border-[#E8EAED] rounded-xl px-4 py-5 text-center">
            <p className="text-sm text-[#3F4A52]">Nenhuma missão em andamento.</p>
            <p className="text-xs text-[#3F4A52]/60 mt-1">As missões são criadas em Campanhas.</p>
          </div>
        ) : (
          <div className="bg-white border border-[#E8EAED] rounded-xl divide-y divide-[#E8EAED]">
            <Row label="Missão" value={`${mission.code} · ${mission.name}`} />
            <Row label="Projeto" value={mission.projectName || "—"} />
            <Row label="Área piloto" value={mission.areaPilotoName || "—"} />
            <Row label="Exemplares registrados" value={String(mission.registeredTreeCount)} />
            <Row label="Meta" value={mission.targetTreeCount !== null ? String(mission.targetTreeCount) : "—"} />
            <Row label="Quadras concluídas" value={`${mission.quadrasConcluidas} de ${mission.quadraCount}`} />
            <Row label="Período" value={`${formatDate(mission.startDate)} a ${formatDate(mission.endDate)}`} />
          </div>
        )}
      </div>

      <div>
        <h3 className="text-sm font-heading font-bold text-[#3F4A52] mb-3">Localização do aparelho</h3>
        <div className="bg-white border border-[#E8EAED] rounded-xl divide-y divide-[#E8EAED]">
          <Row
            label="Coordenadas"
            value={gps.latitude !== null && gps.longitude !== null ? `${gps.latitude.toFixed(6)}, ${gps.longitude.toFixed(6)}` : "—"}
          />
          <Row label="Precisão" value={gps.accuracy !== null ? `± ${Math.round(gps.accuracy)} m` : "—"} />
          {gps.error && <Row label="Situação" value={gps.error} />}
        </div>
      </div>
    </main>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3">
      <span className="text-sm text-[#3F4A52]">{label}</span>
      <span className="text-sm font-heading font-bold text-[#3F4A52] text-right truncate">{value}</span>
    </div>
  );
}
